import { useEffect, useMemo, useState } from "react";

export interface SequentialTypewriterOptions {
  /** ms por caractere digitado. Padrão: 18. */
  charDelay?: number;
  /** Pausa entre um segmento e o próximo, em ms. Padrão: `charDelay * 10`. */
  segmentPause?: number;
}

export interface SequentialTypewriterResult {
  /** Trecho já visível de cada segmento, na mesma ordem de `segments`. */
  typed: string[];
  /** `true` quando todos os segmentos terminaram de ser digitados. */
  done: boolean;
  /** `true` se o segmento `index` está sendo digitado neste momento. */
  isTyping: (index: number) => boolean;
}

/**
 * Digita uma lista de textos em sequência (efeito "máquina de escrever").
 *
 * Os segmentos são digitados um após o outro, com uma pausa entre eles. Quem
 * prefere menos movimento (`prefers-reduced-motion`) vê tudo de uma vez e o
 * `done` é liberado imediatamente.
 *
 * @param segments Textos a digitar, em ordem.
 * @param enabled  Liga a animação (ex.: `open` do dialog). Ao alternar, reinicia.
 * @param options  Ajustes finos de velocidade.
 */
export function useSequentialTypewriter(
  segments: string[],
  enabled: boolean,
  options: SequentialTypewriterOptions = {},
): SequentialTypewriterResult {
  const { charDelay = 18, segmentPause = charDelay * 10 } = options;

  const [typed, setTyped] = useState<string[]>(() => segments.map(() => ""));
  const [done, setDone] = useState(false);

  // Conteúdo (não identidade) dos segmentos: reinicia a animação quando o texto
  // muda, mas é estável se o chamador recriar o array com o mesmo conteúdo.
  const segmentsKey = useMemo(() => segments.join(" "), [segments]);

  useEffect(() => {
    if (!enabled) return;

    // Acessibilidade: quem pede menos movimento vê tudo de uma vez e libera o `done`.
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setTyped([...segments]);
      setDone(true);
      return;
    }

    setTyped(segments.map(() => ""));
    setDone(false);

    let seg = 0;
    let char = 0;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (seg >= segments.length) {
        setDone(true);
        return;
      }

      const current = segments[seg];
      char += 1;
      setTyped((prev) => {
        const next = [...prev];
        next[seg] = current.slice(0, char);
        return next;
      });

      if (char >= current.length) {
        seg += 1;
        char = 0;
        timer = setTimeout(tick, segmentPause);
      } else {
        timer = setTimeout(tick, charDelay);
      }
    };

    timer = setTimeout(tick, charDelay);
    return () => clearTimeout(timer);
    // Reinicia ao abrir/fechar, mudar conteúdo dos segmentos ou velocidade.
    // `segments` é referenciado por conteúdo via `segmentsKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, segmentsKey, charDelay, segmentPause]);

  const isTyping = (index: number) =>
    !done && typed[index]?.length > 0 && typed[index].length < (segments[index]?.length ?? 0);

  return { typed, done, isTyping };
}
